import type {
  HITLRequestConfirmationMessage,
  TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import {
  findUserConfirmationForHITL,
  getHITLTargetIds,
  isDataOrConfirmationPart,
  isHITLRequestConfirmation,
  parseBaseDataMessage,
} from './hitl-confirmation-matching';

type HitlRequestPart = {
  partId: string | undefined;
  hitlMessage: HITLRequestConfirmationMessage;
};

function readHitlRequestParts(
  parts: TerraUIMessage['parts'] | undefined,
): HitlRequestPart[] {
  return (parts ?? []).flatMap((part) => {
    if (!isDataOrConfirmationPart(part) || !part.data) {
      return [];
    }

    const parsed = parseBaseDataMessage(part.data);

    return parsed && isHITLRequestConfirmation(parsed)
      ? [{ partId: part.id, hitlMessage: parsed }]
      : [];
  });
}

function readActionToolCallIds(
  hitlMessage: HITLRequestConfirmationMessage,
): string[] {
  return (hitlMessage.hitlRequestConfirmationPrompt?.actionRequests ?? [])
    .map((actionRequest) => actionRequest.toolCallId ?? actionRequest.id)
    .filter((actionId): actionId is string => Boolean(actionId));
}

export function collectPendingHitlToolCallIds(
  parts: TerraUIMessage['parts'],
): Set<string> {
  return new Set(
    readHitlRequestParts(parts)
      .filter(({ hitlMessage }) => !hitlMessage.decision)
      .flatMap(({ hitlMessage }) => readActionToolCallIds(hitlMessage)),
  );
}

export function collectCrossMessagePendingHitlToolCallIds(
  messages: TerraUIMessage[],
): Set<string> {
  return new Set(
    messages.flatMap((message, messageIndex) =>
      readHitlRequestParts(message.parts)
        .filter(
          ({ partId, hitlMessage }) =>
            !hitlMessage.decision &&
            !findUserConfirmationForHITL({
              messages,
              startIndex: messageIndex + 1,
              targetMessageIds: getHITLTargetIds({
                dataMessage: hitlMessage,
                messagePartId: partId,
              }),
            }).decision,
        )
        .flatMap(({ hitlMessage }) => readActionToolCallIds(hitlMessage)),
    ),
  );
}
